import { firebaseAuth } from "../firebase";
import appConstants from "../appConstants";

/**
 * Secured Seniors at Home consent client (NCARE-778).
 *
 * Consent is captured by the server-owned saveConsent endpoint so the browser
 * never reads or writes the `consents` collection directly. The endpoint owns
 * the carer identity, the clinic and the patient authorization; the client only
 * supplies the patient ids it loaded during registration.
 */

const MISSING_AUTH_MESSAGE = "You must be signed in to confirm consent.";

async function callConsentEndpoint(name, payload, fallbackMessage) {
  const currentUser = firebaseAuth && firebaseAuth.currentUser;
  if (!currentUser) {
    throw new Error(MISSING_AUTH_MESSAGE);
  }

  const idToken = await currentUser.getIdToken();
  const response = await fetch(
    `${appConstants.firebaseFunctionsUrl}${name}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(payload),
    }
  );

  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result || result.success !== true) {
    const error = new Error(
      (result && result.error) || fallbackMessage
    );
    error.code = result && result.code;
    error.status = response.status;
    throw error;
  }
  return result.data;
}

/**
 * Loads the Review Consent model through the secured endpoint. The endpoint
 * re-verifies the caller and authorizes every requested patient, so route
 * state is only a hint and never proof of authorization.
 */
export const getConsentReview = ({ patientIds }) =>
  callConsentEndpoint(
    "getConsentReview",
    { patientIds },
    "Could not load your review details. Please try again."
  );

/**
 * Persists the carer's consent for the given patients. Ownership fields
 * (carerId, clinicId, patientCount, timestamps) are deliberately omitted: the
 * backend derives them from the verified token and the patient records.
 */
export const saveConsent = ({ patientIds }) =>
  callConsentEndpoint(
    "saveConsent",
    { patientIds },
    "Could not save your confirmation. Please try again."
  );

export const getConsentStatus = () =>
  callConsentEndpoint(
    "getConsentStatus",
    {},
    "Could not verify your registration status. Please try again."
  );

export const completeOnboarding = () =>
  callConsentEndpoint(
    "completeOnboarding",
    {},
    "Couldn't finish setting up your home support account. Please try again."
  );
